import { Component, TemplateFunction } from '@ribajs/core'
import { scrollToPosition } from '@ribajs/utils'
import { ShopifyProductService, SbLocaleService } from '../services/index.js'
import { SbCardProductComponent } from './sb-card-product.component.js'
import { SbProductComponent } from './sb-product.component.js'
import type {
  ShopifyProductVariant,
  ShopifyProduct,
  CardProductOption,
  CardProductOptionValue,
  VariantSelectorData,
  // VariantOverlayData,
} from '../types/index.js'

const NO_AUTO_SELECT_TYPES_CARD = ['Kaffee', 'Kakao']

/**
 * The types for the scope
 */
interface Scope extends VariantSelectorData {
  // OPTIONS
  /** The type of the variant selector */
  type: 'card' | 'pdp' | 'overlay'
  /** The variant id to preselect */
  variantId: number | undefined
  options: CardProductOption[]
  option1: CardProductOption
  option2?: CardProductOption
  /** Show the options */
  showOptions: boolean
  /** Show the second options */
  showSecondOptions: boolean
  /** Show the more options */
  showMoreOptions: boolean
  /** Is true if the user has chosen an option */
  optionSelected: boolean
  /** Current section id */
  updateUrl: boolean

  // METHODS
  chooseOption: SbVariantSelectorComponent['chooseOption']
  chooseMainOption: SbVariantSelectorComponent['chooseMainOption']

  showSecondOptionsMenu: SbVariantSelectorComponent['showSecondOptionsMenu']
  hideSecondOptionMenu: SbVariantSelectorComponent['hideSecondOptionMenu']
  showMoreOptionsMenu: SbVariantSelectorComponent['showMoreOptionsMenu']
  hideMoreOptionsMenu: SbVariantSelectorComponent['hideMoreOptionsMenu']
  undoOptions: SbVariantSelectorComponent['undoOptions']
  getOptionTitle: SbVariantSelectorComponent['getOptionTitle']
  showPackageContentsFallback: SbVariantSelectorComponent['showPackageContentsFallback']

  // TEMPLATES

  // Elements
  options1El?: HTMLDivElement
  options2El?: HTMLDivElement
}

const emptyOption: CardProductOption = {
  name: '',
  position: -1,
  autoSelect: false,
  values: [],
  more: [],
  all: [],
}

/**
 * A product variant selector, used in the card product, product detail page and in the variant overlay popup.
 *
 * Note: This component also handles auto variant selections and
 * the selection of the first available variant if no variant is selected.
 */
export class SbVariantSelectorComponent extends Component {
  public static tagName = 'sb-variant-selector'

  lifecycleOptions = {
    ignore: true,
  }

  protected localeService = SbLocaleService.getSingleton()

  public cardProductEls: SbCardProductComponent[] = []

  protected parentProductComponent?: SbProductComponent | SbCardProductComponent

  public scope: Scope = {
    // OPTIONS
    type: 'card',
    handle: '',
    product: undefined,
    variantId: undefined,
    variant: undefined,
    options: [],
    option1: {
      ...emptyOption,
    },
    option2: undefined,
    showOptions: false,
    showSecondOptions: false,
    showMoreOptions: false,
    optionSelected: false,

    updateUrl: false,

    // METHODS
    chooseOption: this.chooseOption,
    chooseMainOption: this.chooseMainOption,
    showSecondOptionsMenu: this.showSecondOptionsMenu,
    hideSecondOptionMenu: this.hideSecondOptionMenu,
    showMoreOptionsMenu: this.showMoreOptionsMenu,
    hideMoreOptionsMenu: this.hideMoreOptionsMenu,
    undoOptions: this.undoOptions,
    getOptionTitle: this.getOptionTitle,
    showPackageContentsFallback: this.showPackageContentsFallback,
    // TEMPLATES
  }

  static get observedAttributes(): string[] {
    return [
      'handle',
      'product',
      'variant',
      'variant-id',
      'update-url',
      'type',
      'option-selected',
    ]
  }

  /**
   * The number of selections needed to select a variant
   * @returns
   */
  protected get neededSelections(): number {
    return this.scope.options.length
  }

  /**
   * The total number of options
   */
  protected get totalOptions() {
    return this.scope.option1.all.length + (this.scope.option2?.all.length || 0)
  }

  /**
   * The number of options the user can select (not auto select options like "pack")
   */
  protected get totalUserOptions() {
    let total = 0
    if (!this.scope.option1.autoSelect) {
      total += this.scope.option1.all.length
    }
    if (this.scope.option2 && !this.scope.option2.autoSelect) {
      total += this.scope.option2.all.length
    }
    return total
  }

  constructor() {
    super()
  }

  protected requiredAttributes(): string[] {
    return ['handle', 'product', 'type']
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbVariantSelectorComponent.observedAttributes)
  }

  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  /**
   * Prepare the options for the variant selector, if we
   * @param product The product
   */
  protected async prepareOptions(product: ShopifyProduct) {
    this.scope.options = []
    const optionNamesPackI18n =
      await ShopifyProductService.getOptionNames('pack')

    for (const opt of product.options) {
      let autoSelect = false
      // Set auto select for the option if we are in card or overlay mode and the option name is "pack" in it's translated form
      if (this.scope.type === 'card' || this.scope.type === 'overlay') {
        // Auto select the option if the option name is "pack" in it's translated form
        autoSelect = optionNamesPackI18n.some(
          optionNamePackI18n =>
            opt.name.toLowerCase() === optionNamePackI18n.toLowerCase(),
        )
      }
      // Split options to `values` (max 3), more (the other more than 3) and all
      this.scope.options.push(
        ShopifyProductService.splitOption(opt, autoSelect),
      )
    }

    this.scope.option1 = this.scope.options[0]
    this.scope.option2 = this.scope.options[1] || undefined

    this.setShowOptions()
    await this.setInitialOptionsAvailability()

    if (!this.scope.option1) {
      throw new Error('A product must have at least one option')
    }
  }

  /**
   * Set the show options flag based on the number of options.
   * If we have more than 1 option, we show the options menu.
   */
  protected setShowOptions() {
    this.scope.showOptions = this.totalUserOptions > 1
  }

  /**
   * Select the first existing option.
   * Used as a fallback if no option is available.
   * @param option
   * @returns
   */
  protected chooseFirstExistingOption(option: CardProductOption) {
    let optionFound = false
    const availableOptions = option.all.filter(option => option.exists)

    if (availableOptions[0]) {
      this.setActiveOption(availableOptions[0], option)
      optionFound = true
    }

    return optionFound
  }

  /**
   * Select the first existing and available option
   * @param option
   * @returns True if the option value was selected
   */
  protected chooseFirstAvailableOption(option: CardProductOption) {
    let optionFound = false
    const availableOptions = option.all.filter(
      option => option.exists && option.available,
    )

    if (availableOptions[0]) {
      this.setActiveOption(availableOptions[0], option)
      optionFound = true
    }

    return optionFound
  }

  protected setActiveOption(
    optionValue: CardProductOptionValue,
    option: CardProductOption,
  ) {
    this.deactivateAllOptionValues(option)
    if (optionValue.hidden) {
      console.warn('Option value is hidden', optionValue)
      return
    }
    optionValue.active = true
  }

  public setActiveOptionByVariant(variant?: ShopifyProductVariant) {
    this.deactivateAllOptions()

    if (!variant) {
      return
    }

    for (const option of this.scope.options) {
      for (const optionValue of option.all) {
        if (variant.options.includes(optionValue.value)) {
          this.setActiveOption(optionValue, option)
        }
      }
    }

    for (const optionValue of this.scope.option1.all) {
      if (variant.options.includes(optionValue.value)) {
        this.setActiveOption(optionValue, this.scope.option1)
      }
    }

    if (this.scope.option2) {
      for (const optionValue of this.scope.option2.all) {
        if (variant.options.includes(optionValue.value)) {
          this.setActiveOption(optionValue, this.scope.option2)
        }
      }

      if (this.scope.option2.all.length && !this.scope.option2.autoSelect) {
        this.showSecondOptionsMenu()
      }
    }

    this.scope.optionSelected = true

    this.setOptionsAvailability()
  }

  /**
   * Used by `deactivateAllOptions`
   * @param option
   */
  protected deactivateAllOptionValues(option: CardProductOption) {
    if (option.all.length) {
      for (const val of option.all) {
        val.active = false
      }
    }
  }

  /**
   * Disable all option values (used to activate a new option value without active multiple option values of the same option)
   */
  protected deactivateAllOptions() {
    for (const option of this.scope.options) {
      this.deactivateAllOptionValues(option)
    }
    this.deactivateAllOptionValues(this.scope.option1)
    if (this.scope.option2) {
      this.deactivateAllOptionValues(this.scope.option2)
    }
    this.scope.optionSelected = false
  }

  /**
   * Get an array with all selected product options
   */
  protected getActiveOptions() {
    const activeOptions: CardProductOptionValue[] = []
    for (const option of this.scope.options) {
      for (const val of option.all) {
        if (val.active) {
          activeOptions.push(val)
        }
      }
    }
    return activeOptions
  }

  /**
   * Used by `setOptionsAvailability` to check a single option value for availability
   * @param selectedOptions The selected option values
   * @param change If true, the option value will be changed
   * @returns
   */
  protected setOptionAvailability(
    selectedOptions: CardProductOptionValue[],
    change = true,
  ) {
    const optionValueToCheck = selectedOptions[selectedOptions.length - 1]
    // Get product variant of this options
    const variant =
      ShopifyProductService.getVariantOfOptions(
        this.scope.product!,
        selectedOptions,
      ) || undefined

    const hidden = optionValueToCheck.hidden
    const exists = variant !== undefined && !hidden
    const available = exists && variant?.available

    if (change) {
      optionValueToCheck.hidden = hidden
      optionValueToCheck.exists = exists
      optionValueToCheck.available = available
      return optionValueToCheck
    }

    return {
      hidden,
      exists,
      available,
    }
  }

  protected getOptionsForOptionValue(
    optionValue: CardProductOptionValue,
  ): { position: 1 | 2; option: CardProductOption } | null {
    if (this.scope.option1.all.includes(optionValue)) {
      return {
        position: 1,
        option: this.scope.option1,
      }
    }

    if (this.scope.option2 && this.scope.option2.all.includes(optionValue)) {
      return {
        position: 2,
        option: this.scope.option2,
      }
    }
    return null
  }

  /**
   * Set the availability of the options based on the main / first option values
   * - Compares each combination of option values
   * @returns
   */
  async setInitialOptionsAvailability() {
    // If we have only one option, check all `option1` values for availability
    if (this.neededSelections === 1) {
      for (const o1 of this.scope.option1.all) {
        this.setOptionAvailability([o1])
      }
      return
    }

    // If two options are needed, check option combinations for availability
    if (this.neededSelections === 2 && this.scope.option2) {
      for (const o1 of this.scope.option1.all) {
        const allCombinations = {
          unavailable: true,
          notExists: true,
          hidden: true,
        }

        for (const o2 of this.scope.option2.all) {
          const availability = this.setOptionAvailability([o1, o2], false)
          allCombinations.unavailable &&= !availability.available
          allCombinations.notExists &&= !availability.exists
          allCombinations.hidden &&= availability.hidden
        }

        // If all combinations are unavailable, not exists or hidden, we can also set the `option1` value to this
        o1.available = !allCombinations.unavailable
        o1.exists = !allCombinations.notExists
        o1.hidden = allCombinations.hidden
      }
    }
  }

  /**
   * Set the availability of the options based on the selected options
   * @returns
   */
  protected setOptionsAvailability() {
    if (!this.scope.product) return

    const selectedOptions = this.getActiveOptions()

    if (this.neededSelections >= 2) {
      if (selectedOptions.length >= 1) {
        const selectedOptionVal = selectedOptions[0]
        const selectedPosition =
          this.getOptionsForOptionValue(selectedOptionVal)?.position
        if (!selectedPosition) {
          console.error('No selected option found for', selectedOptionVal)
          return
        }

        const unselectedOptions =
          selectedPosition === 1 && this.scope.option2
            ? this.scope.option2.all
            : this.scope.option1.all

        for (const optionValue of unselectedOptions) {
          this.setOptionAvailability([selectedOptionVal, optionValue])
        }
      }
    }
  }

  /**
   * Show all options (more than 3 options), also the hidden one (x More)
   */
  public showMoreOptionsMenu() {
    //Set maxWidth so that the options are not wider than the parent element
    if (this.classList.contains('useFixedWidth')) {
      this.style.maxWidth = this.parentNode?.parentElement?.clientWidth + 'px'
    }
    this.scope.showMoreOptions = true

    // Auto scroll to end position
    if (this.scope.options1El) {
      scrollToPosition(this.scope.options1El, 'end', 'horizontal')
    }
    if (this.scope.options2El) {
      scrollToPosition(this.scope.options2El, 'end', 'horizontal')
    }
  }

  /**
   * Hide the hidden options again (show max. 3 options)
   */
  public hideMoreOptionsMenu() {
    this.scope.showMoreOptions = false
  }

  /**
   * Show the second variant options
   */
  public showSecondOptionsMenu() {
    this.scope.showSecondOptions = true
  }

  /**
   * Hide the second variant options again
   */
  public hideSecondOptionMenu() {
    this.scope.showSecondOptions = false
  }

  /**
   * Reset selected options
   */
  public undoOptions() {
    this.deactivateAllOptions()
    this.setVariant(undefined)
    this.hideSecondOptionMenu()
    this.hideMoreOptionsMenu()
  }

  /**
   * Alternative version of `chooseOption` but removes the previous variant selection.
   * @param optionValue
   * @param option
   * @param event
   * @returns
   */
  public async chooseMainOption(
    optionValue: CardProductOptionValue,
    option: CardProductOption,
    event?: MouseEvent | TouchEvent,
  ) {
    if (!this.scope.product) {
      throw new Error('Product not set!')
    }

    if (optionValue.active) {
      console.warn('Option value is already active!', optionValue)
      return
    }

    this.deactivateAllOptions()
    await this.chooseOption(optionValue, option, event)
  }

  /**
   * Get the translated title of the option
   * @param option
   * @returns
   */
  public async getOptionTitle(option: CardProductOption) {
    const specialTranslation = await this.localeService._(
      'sections.sb-popup-variant-overlay.select_option_' +
        option.name.toLowerCase(),
      { option: option.name },
    )

    if (specialTranslation) {
      return specialTranslation
    }

    // Fallback to the default translation
    return await this.localeService._(
      'sections.sb-popup-variant-overlay.select_option',
      { option: option.name },
    )
  }

  /**
   * Check whether to show the separate section "Inhalt". This is used for products without an "Inhalt" option that have it set as metafield.
   * @param variant The product variant to check
   * @returns
   */
  public async showPackageContentsFallback(
    variant?: ShopifyProductVariant,
    product?: ShopifyProduct,
  ): Promise<boolean> {
    if (!variant) {
      return false
    }

    const translatedContentLabel =
      await ShopifyProductService.getPackageContentsLabel()

    // If the product has an option with the name "Inhalt", don't show the fallback
    if (
      product?.options.some(option => option.name === translatedContentLabel)
    ) {
      return false
    }

    return (
      !this.scope.showOptions &&
      !this.scope.showSecondOptions &&
      ShopifyProductService.hasPackageContentsFallback(variant)
    )
  }

  /**
   * Called if the user clicks on an option.
   * If there are more options than one the next options menu will be displayed as soon as the user has clicked on one of the first options
   * @param optionValue
   * @param option
   * @param event
   * @returns
   */
  public async chooseOption(
    optionValue: CardProductOptionValue,
    option: CardProductOption,
    event?: MouseEvent | TouchEvent,
  ) {
    if (!this.scope.product) {
      throw new Error('Product not set!')
    }

    if (optionValue.active) {
      console.warn('Option value is already active!', optionValue)
      return
    }

    this.setActiveOption(optionValue, option)

    this.setOptionsAvailability()

    let selectedOptions = this.getActiveOptions()

    if (this.neededSelections !== selectedOptions.length) {
      if (option === this.scope.option1 && this.scope.option2) {
        const found =
          this.chooseFirstAvailableOption(this.scope.option2) ||
          this.chooseFirstExistingOption(this.scope.option2)
        if (found) {
          this.hideSecondOptionMenu()
        }
      }
      if (option === this.scope.option2) {
        const found =
          this.chooseFirstAvailableOption(this.scope.option1) ||
          this.chooseFirstExistingOption(this.scope.option1)
        if (found) {
          this.hideSecondOptionMenu()
        }
      }
    }

    selectedOptions = this.getActiveOptions()

    if (selectedOptions.length !== this.neededSelections) {
      this.showSecondOptionsMenu()
      this.scope.optionSelected = false
      return
    }

    // Option chosen -> enable "add to cart" button
    this.scope.optionSelected = true

    const variant =
      ShopifyProductService.getVariantOfOptions(
        this.scope.product,
        selectedOptions,
      ) || undefined

    this.setVariant(variant)

    event?.stopPropagation()
  }

  /**
   * Select a variant by its id
   * @param id The variant id to select the variant
   * @returns The selected variant if found, otherwise undefined
   */
  protected setVariantById(id: number) {
    if (!this.scope.product) return null
    const variant = ShopifyProductService.getVariantById(this.scope.product, id)
    if (!variant) {
      console.warn(
        `[SbVariantSelectorComponent.setVariantById] No variant found for product handle "${this.scope.handle}" with variant id: "${id}"`,
        this.scope.product,
      )
      return null
    }

    return this.setVariant(variant)
  }

  protected getVariantURL() {
    const url = new URL(window.location.toString())
    return url.searchParams.get('variant')
  }

  /**
   * Remove the variant id from URL search parameter
   */
  protected clearVariantIdFromURL() {
    const url = new URL(window.location.toString())
    url.searchParams.delete('variant')
    window.history.replaceState(null, '', url.toString())
  }

  /**
   * Set the variant id from the URL search parameter
   * @returns The variant if found, otherwise null
   */
  protected setVariantIdFromURL(): ShopifyProductVariant | null {
    if (!this.scope.product) {
      return null
    }

    const variantId = this.getVariantURL()
    if (!variantId) {
      return null
    }

    const variant = ShopifyProductService.getVariantById(
      this.scope.product,
      variantId,
    )

    if (variant) {
      this.scope.variantId = variant.id
      this.setVariant(variant)
      return variant
    }

    console.warn(
      `[SbVariantSelectorComponent.setVariantIdFromURL] Invalid variant id ${variantId}`,
    )
    this.clearVariantIdFromURL()

    return null
  }

  /**
   * Auto select the variant
   * @returns True if a variant was selected, otherwise false
   */
  protected async autoSelectVariant(): Promise<ShopifyProductVariant | null> {
    let foundVariant: ShopifyProductVariant | null = null

    if (!this.scope.product) {
      throw new Error('Product not set!')
    }

    // A specific variant should be preselected
    if (this.scope.variantId) {
      foundVariant = this.setVariantById(this.scope.variantId)
      if (!foundVariant) {
        console.warn(
          `[SbVariantSelectorComponent.autoSelectVariant] Invalid variant id ${this.scope.variantId}`,
        )
        this.scope.variantId = undefined
      }
    }

    if (!foundVariant) {
      if (this.scope.type === 'pdp') {
        foundVariant = this.setVariantIdFromURL()

        if (foundVariant) {
          return foundVariant
        }

        // Variant id invalid, reset it
        this.scope.variantId = undefined

        foundVariant = this.selectFirstAvailableVariant()
      } else if (this.scope.type === 'card' || this.scope.type === 'overlay') {
        if (!NO_AUTO_SELECT_TYPES_CARD.includes(this.scope.product?.type)) {
          foundVariant = this.selectFirstAvailableVariant()
        }
      } else {
        console.warn('Unknown variant selector type:', this.scope.type)
      }
    }

    this.scope.variantId = foundVariant?.id || undefined

    return foundVariant
  }

  protected selectFirstAvailableVariant(): ShopifyProductVariant | null {
    const variant = ShopifyProductService.getFirstAvailableVariant(
      this.scope.product!,
    )
    if (!variant) {
      console.error(
        new Error(
          `[SbVariantSelectorComponent.selectFirstAvailableVariant] No variant found for ${this.scope.product?.handle}!`,
        ),
        this.scope.product,
      )
      return null
    }

    return this.setVariant(variant)
  }

  protected autoSelectOptions() {
    let anyOptionSelected = false
    for (const option of this.scope.options) {
      if (option.autoSelect) {
        anyOptionSelected =
          this.chooseFirstAvailableOption(option) ||
          this.chooseFirstExistingOption(option)
      }
    }

    if (this.scope.option1.autoSelect) {
      anyOptionSelected =
        this.chooseFirstAvailableOption(this.scope.option1) ||
        this.chooseFirstExistingOption(this.scope.option1)

      if (
        anyOptionSelected &&
        this.scope.option2 &&
        !this.scope.option2.autoSelect
      ) {
        this.showSecondOptionsMenu()
      }
    }

    if (this.scope.option2 && this.scope.option2.autoSelect) {
      anyOptionSelected =
        this.chooseFirstAvailableOption(this.scope.option2) ||
        this.chooseFirstExistingOption(this.scope.option2)

      if (anyOptionSelected) {
        this.hideSecondOptionMenu()
      }
    }

    this.scope.optionSelected ||= anyOptionSelected

    const selectedOptions = this.getActiveOptions()

    if (selectedOptions.length !== this.neededSelections) {
      return anyOptionSelected
    }

    const variant =
      ShopifyProductService.getVariantOfOptions(
        this.scope.product!,
        selectedOptions,
      ) || undefined

    this.setVariant(variant)

    return anyOptionSelected
  }

  protected setVariantURL() {
    if (!this.scope.variant || !this.scope.updateUrl) return
    const url = new URL(window.location.toString())
    if (this.scope.variant?.id && !url.searchParams.get('variant')) {
      url.searchParams.set('variant', this.scope.variant.id.toString())
    }
    window.history.replaceState(
      null,
      '',
      `${this.scope.product?.handle}?variant=${this.scope.variant?.id}`,
    )
  }

  protected triggerVariantChangeEvent() {
    const event = new CustomEvent('variant-change', {
      detail: {
        variant: this.scope.variant,
      },
    })
    this.dispatchEvent(event)
  }

  /**
   * Set the variant and sync it to all child components
   * @param variant The variant to set
   * @returns The variant if set, otherwise null
   */
  public setVariant(
    variant?: ShopifyProductVariant,
  ): ShopifyProductVariant | null {
    const oldVariant = this.scope.variant
    if (
      variant &&
      (this.scope.variant === variant ||
        (this.scope.variant?.id === variant?.id &&
          ShopifyProductService.equal(this.scope.variant, variant)))
    ) {
      return this.scope.variant
    }

    this.scope.variant = variant

    if (variant && oldVariant?.id !== variant?.id) {
      this.onVariantChanged(variant)
    }

    return this.scope.variant || null
  }

  protected async onProductChanged(oldVariant?: ShopifyProductVariant) {
    if (!this.scope.product || !this.scope.product.sbIsPrepared) {
      return
    }

    this.scope.handle = this.scope.handle || this.scope.product?.handle
    if (this.scope.product.options) {
      await this.prepareOptions(this.scope.product)
      if (this.scope.type === 'card') {
        this.autoSelectOptions()
      }
    }

    // Restore the selected variant
    if (oldVariant) {
      this.onVariantChanged(oldVariant, false)
    } else {
      this.autoSelectVariant()
    }
  }

  public async onVariantChanged(
    variant?: ShopifyProductVariant,
    triggerEvent = true,
  ) {
    if (!this.scope.product) return

    if (variant && ShopifyProductService.hiddenOnMarket(variant.metafields)) {
      console.warn(
        `[SbVariantSelectorComponent.onVariantChanged] Variant is hidden on current market!`,
        this.scope.variant,
      )
      this.scope.variantId = undefined
      this.setVariant(undefined)
      this.undoOptions()
      return
    }

    this.setVariant(variant)
    this.setActiveOptionByVariant(variant)
    if (this.scope.type === 'pdp') {
      this.setVariantURL()
    }
    if (triggerEvent) {
      this.triggerVariantChangeEvent()
    }
  }

  protected onHandleChanged() {
    this.cardProductEls = Array.from(
      document.querySelectorAll(
        `${SbCardProductComponent.tagName}[handle="${this.scope.handle}"]`,
      ),
    )
  }

  protected parsedAttributeChangedCallback(
    attributeName: string,
    oldValue: any,
    newValue: any,
    namespace: string | null,
  ): void {
    const oldVariant = this.scope.variant
    super.parsedAttributeChangedCallback(
      attributeName,
      oldValue,
      newValue,
      namespace,
    )
    if (newValue === oldValue) return
    if (attributeName === 'product') {
      this.onProductChanged(oldVariant)
    }
    if (attributeName === 'handle') {
      this.onHandleChanged()
    }
    if (attributeName === 'variant') {
      this.onVariantChanged(newValue, true) // TODO: Trigger event can result in infinite loop here but is needed for multipack
    }
  }

  async beforeBind(): Promise<any> {
    await super.beforeBind()
    this.addEventListeners()
    this.setOptionsAvailability()
    this.parentProductComponent = this.closest(
      `${SbProductComponent.tagName}, ${SbCardProductComponent.tagName}`,
    ) as SbProductComponent | SbCardProductComponent | undefined
  }

  async afterBind() {
    this.classList.add(`${SbVariantSelectorComponent.tagName}-ready`)
    await super.afterBind()
  }

  protected addEventListeners() {}

  protected removeEventListeners() {}

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
